import { fixedUi } from '@/i18n/fixedUi';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
} from '@/utils/platformShopScope';
import './PlatformShopDimensionTabs.css';

function resolveShopLabel(shop) {
  return String(shop?.shopName || '').trim() || `店铺 ${shop?.id ?? ''}`;
}

function resolveCount(counts, id) {
  if (!counts) return null;
  const raw = counts[id] ?? counts[String(id)];
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * 按容器可容纳宽度计算可见店铺数（不含「全部」）。
 * @param {number[]} shopWidths
 * @param {number} containerWidth
 * @param {number} allWidth 「全部」宽度；不展示时传 0
 * @param {number} moreWidth 「更多」按钮宽度
 */
export function fitShopDimensionVisibleCount(shopWidths, containerWidth, allWidth, moreWidth) {
  const widths = Array.isArray(shopWidths) ? shopWidths : [];
  const container = Math.max(0, Number(containerWidth) || 0);
  const all = Math.max(0, Number(allWidth) || 0);
  const more = Math.max(0, Number(moreWidth) || 0);
  if (!widths.length) return 0;
  if (container <= 0) return widths.length;

  let used = all;
  let count = 0;
  for (let i = 0; i < widths.length; i += 1) {
    const next = used + widths[i];
    if (next <= container) {
      used = next;
      count += 1;
    } else {
      break;
    }
  }
  if (count >= widths.length) return widths.length;

  // 放不下时预留「更多」，重新贪心
  used = all + more;
  count = 0;
  for (let i = 0; i < widths.length; i += 1) {
    const next = used + widths[i];
    if (next <= container) {
      used = next;
      count += 1;
    } else {
      break;
    }
  }
  return Math.max(0, count);
}

/**
 * 将选中店固定露在可见条：前 maxVisible-1 按原序，最后一位留给选中店（若其本在溢出区）。
 */
export function splitShopDimensionTabs(shops, selectedId, maxVisible) {
  const list = Array.isArray(shops) ? shops : [];
  if (!Number.isFinite(maxVisible) || maxVisible >= list.length) {
    return { visible: list, overflow: [] };
  }
  const limit = Math.max(0, Math.floor(maxVisible));
  if (limit <= 0) {
    return { visible: [], overflow: list };
  }
  if (list.length <= limit) {
    return { visible: list, overflow: [] };
  }

  const selected = String(selectedId || '').trim();
  const selectedInList = selected
    && !isAllShopsScope(selected)
    && list.some((shop) => String(shop.id) === selected);

  if (!selectedInList) {
    return {
      visible: list.slice(0, limit),
      overflow: list.slice(limit),
    };
  }

  const selectedIndex = list.findIndex((shop) => String(shop.id) === selected);
  if (selectedIndex < limit) {
    return {
      visible: list.slice(0, limit),
      overflow: list.slice(limit),
    };
  }

  if (limit === 1) {
    return {
      visible: [list[selectedIndex]],
      overflow: list.filter((shop) => String(shop.id) !== selected),
    };
  }

  const head = list.slice(0, limit - 1);
  const selectedShop = list[selectedIndex];
  const visibleIds = new Set([...head.map((s) => String(s.id)), selected]);
  const overflow = list.filter((shop) => !visibleIds.has(String(shop.id)));
  return {
    visible: [...head, selectedShop],
    overflow,
  };
}

/**
 * 店铺维度 Tab（样式对齐 spo-status-tabs）
 * 按行宽尽量铺满店铺；放不下时用「更多」下拉（含筛选），面板挂载到 body。
 */
export default function PlatformShopDimensionTabs({
  shops = [],
  value,
  onChange,
  counts = null,
  showAll = true,
  allLabel = '全部',
  /** 至少多少家店才显示「全部」；自动规则页可传 0 以便空列表仍显示「全部店铺」 */
  minShopsForAll = 2,
  disabled = false,
  ariaLabel = '店铺维度',
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [fitCount, setFitCount] = useState(null);
  const [panelStyle, setPanelStyle] = useState(null);

  const rowRef = useRef(null);
  const measureRef = useRef(null);
  const moreBtnRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  const active = isAllShopsScope(value)
    ? PLATFORM_ALL_SHOPS_ID
    : String(value || '');

  const shopList = useMemo(
    () => (Array.isArray(shops) ? shops : []).map((shop) => ({
      id: String(shop.id),
      shopName: resolveShopLabel(shop),
      shopType: shop.shopType || '',
      platformCode: shop.platformCode || '',
    })),
    [shops],
  );

  const showAllTab = showAll && shopList.length >= Number(minShopsForAll || 0);
  const maxVisible = fitCount == null ? shopList.length : fitCount;
  const { visible, overflow } = useMemo(
    () => splitShopDimensionTabs(shopList, active, maxVisible),
    [shopList, active, maxVisible],
  );
  const hasMore = overflow.length > 0;

  const filteredMoreShops = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    const source = q ? shopList : overflow;
    if (!q) return source;
    return source.filter((shop) => {
      const hay = `${shop.shopName} ${shop.id} ${shop.shopType} ${shop.platformCode}`.toLowerCase();
      return hay.includes(q);
    });
  }, [query, shopList, overflow]);

  const closeMore = useCallback(() => {
    setMoreOpen(false);
    setQuery('');
  }, []);

  const measureFit = useCallback(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;

    const containerWidth = row.clientWidth;
    const allEl = measure.querySelector('[data-measure="all"]');
    const moreEl = measure.querySelector('[data-measure="more"]');
    const shopEls = measure.querySelectorAll('[data-measure="shop"]');
    const allWidth = showAllTab ? (allEl?.getBoundingClientRect?.().width || 0) : 0;
    const moreWidth = moreEl?.getBoundingClientRect?.().width || 0;
    const shopWidths = Array.from(shopEls).map((el) => el.getBoundingClientRect().width || 0);
    const next = fitShopDimensionVisibleCount(shopWidths, containerWidth, allWidth, moreWidth);
    setFitCount((prev) => (prev === next ? prev : next));
  }, [showAllTab]);

  useLayoutEffect(() => {
    measureFit();
  }, [measureFit, shopList, counts, allLabel, showAllTab]);

  useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measureFit);
      return () => window.removeEventListener('resize', measureFit);
    }
    const ro = new ResizeObserver(() => measureFit());
    ro.observe(row);
    return () => ro.disconnect();
  }, [measureFit]);

  const updatePanelPosition = useCallback(() => {
    const btn = moreBtnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const panelWidth = Math.min(280, Math.max(220, window.innerWidth * 0.7));
    const gap = 4;
    let left = rect.right - panelWidth;
    left = Math.max(8, Math.min(left, window.innerWidth - panelWidth - 8));
    const top = rect.bottom + gap;
    const maxHeight = Math.min(320, Math.max(160, window.innerHeight - top - 12));
    setPanelStyle({
      position: 'fixed',
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      width: `${Math.round(panelWidth)}px`,
      maxHeight: `${Math.round(maxHeight)}px`,
      zIndex: 10050,
    });
  }, []);

  useLayoutEffect(() => {
    if (!moreOpen) {
      setPanelStyle(null);
      return undefined;
    }
    updatePanelPosition();
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [moreOpen, updatePanelPosition, overflow.length]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (moreBtnRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      closeMore();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMore();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [moreOpen, closeMore]);

  useEffect(() => {
    if (!moreOpen) return;
    const t = window.setTimeout(() => searchRef.current?.focus?.(), 0);
    return () => window.clearTimeout(t);
  }, [moreOpen]);

  useEffect(() => {
    closeMore();
  }, [active, shopList.length, closeMore]);

  if (!shopList.length && !showAllTab) return null;

  const selectShop = (id) => {
    onChange?.(id);
    closeMore();
  };

  const renderCount = (id) => {
    const count = resolveCount(counts, id);
    if (count == null) return null;
    return <span className="spo-status-tab__count">{count}</span>;
  };

  const morePanel = moreOpen && panelStyle && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={panelRef}
        className="spo-shop-dimension-tabs__panel spo-shop-dimension-tabs__panel--portal"
        role="listbox"
        aria-label="更多店铺"
        style={panelStyle}
      >
        <div className="spo-shop-dimension-tabs__search">
          <input
            ref={searchRef}
            type="search"
            className="spo-shop-dimension-tabs__search-input"
            value={query}
            placeholder="筛选店铺名称"
            aria-label="筛选店铺"
            disabled={disabled}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
        <div className="spo-shop-dimension-tabs__list">
          {filteredMoreShops.length === 0 ? (
            <div className="spo-shop-dimension-tabs__empty">无匹配店铺</div>
          ) : (
            filteredMoreShops.map((shop) => {
              const selected = active === shop.id;
              const count = resolveCount(counts, shop.id);
              return (
                <button
                  key={shop.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`spo-shop-dimension-tabs__option${selected ? ' is-selected' : ''}`}
                  disabled={disabled}
                  title={shop.shopName}
                  onClick={() => selectShop(shop.id)}
                >
                  <span className="spo-shop-dimension-tabs__option-name">{shop.shopName}</span>
                  {count != null ? (
                    <span className="spo-shop-dimension-tabs__option-count">{count}</span>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      </div>,
      document.body,
    )
    : null;

  return (
    <div
      ref={rowRef}
      className="spo-status-tabs spo-shop-dimension-tabs"
      role="tablist"
      aria-label={fixedUi(ariaLabel)}
    >
      {/* 隐藏测量条：用于按真实宽度计算本行能放多少店 */}
      <div ref={measureRef} className="spo-shop-dimension-tabs__measure" aria-hidden="true">
        {showAllTab ? (
          <button type="button" className="spo-status-tab" data-measure="all" tabIndex={-1}>
            {fixedUi(allLabel)}
            {renderCount(PLATFORM_ALL_SHOPS_ID)}
          </button>
        ) : null}
        {shopList.map((shop) => (
          <button
            key={`m-${shop.id}`}
            type="button"
            className="spo-status-tab"
            data-measure="shop"
            tabIndex={-1}
          >
            <span className="spo-shop-dimension-tabs__label">{shop.shopName}</span>
            {renderCount(shop.id)}
          </button>
        ))}
        <button
          type="button"
          className="spo-status-tab spo-shop-dimension-tabs__more-btn"
          data-measure="more"
          tabIndex={-1}
        >
          {fixedUi("更多")}
          <span className="spo-status-tab__count">{Math.max(1, shopList.length)}</span>
          <ChevronDown size={14} strokeWidth={2.25} aria-hidden className="spo-shop-dimension-tabs__more-chevron" />
        </button>
      </div>

      {showAllTab ? (
        <button
          type="button"
          role="tab"
          aria-selected={isAllShopsScope(active)}
          className={`spo-status-tab${isAllShopsScope(active) ? ' spo-status-tab--active' : ''}`}
          disabled={disabled}
          onClick={() => selectShop(PLATFORM_ALL_SHOPS_ID)}
        >
          {fixedUi(allLabel)}
          {renderCount(PLATFORM_ALL_SHOPS_ID)}
        </button>
      ) : null}

      {visible.map((shop) => {
        const selected = active === shop.id;
        return (
          <button
            key={shop.id}
            type="button"
            role="tab"
            aria-selected={selected}
            className={`spo-status-tab${selected ? ' spo-status-tab--active' : ''}`}
            disabled={disabled}
            title={shop.shopName}
            onClick={() => selectShop(shop.id)}
          >
            <span className="spo-shop-dimension-tabs__label">{shop.shopName}</span>
            {renderCount(shop.id)}
          </button>
        );
      })}

      {hasMore ? (
        <div className={`spo-shop-dimension-tabs__more${moreOpen ? ' is-open' : ''}`}>
          <button
            ref={moreBtnRef}
            type="button"
            role="tab"
            aria-selected={false}
            aria-haspopup="listbox"
            aria-expanded={moreOpen}
            className={`spo-status-tab spo-shop-dimension-tabs__more-btn${moreOpen ? ' is-open' : ''}`}
            disabled={disabled}
            onClick={() => setMoreOpen((prev) => !prev)}
          >
            {fixedUi("更多")}
            <span className="spo-status-tab__count">{overflow.length}</span>
            <ChevronDown size={14} strokeWidth={2.25} aria-hidden className="spo-shop-dimension-tabs__more-chevron" />
          </button>
        </div>
      ) : null}

      {morePanel}
    </div>
  );
}
